import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { useModuleVisibility } from '@/hooks/useModuleVisibility';

interface ModuleGateProps {
  moduleKey: string;
  children: ReactNode;
  /** Where to redirect when the module is hidden / coming-soon. Defaults to "/". */
  redirectTo?: string;
  /** Friendly module label for the toast. */
  label?: string;
}

/**
 * Wraps a route. If the module is HIDDEN or COMING_SOON for normal users,
 * shows a toast and redirects (default → home). Admins (preview mode) pass through.
 */
const ModuleGate = ({ moduleKey, children, redirectTo = '/', label }: ModuleGateProps) => {
  const { canAccess, status, isAdminOnly } = useModuleVisibility(moduleKey);
  const location = useLocation();

  useEffect(() => {
    if (canAccess && isAdminOnly) {
      // Quiet badge for admins so they know they're in preview
      toast.message(`👁️ Admin preview: ${label || moduleKey} (${status})`, {
        duration: 2500,
      });
    }
  }, [canAccess, isAdminOnly, status, label, moduleKey]);

  if (!canAccess) {
    const name = label || moduleKey;
    if (status === 'COMING_SOON') {
      toast.info(`${name} is coming soon. Stay tuned!`);
    } else {
      toast.message(`${name} is currently unavailable.`);
    }
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  return <>{children}</>;
};

export default ModuleGate;
